import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { permissions, rolePermissions, type Role } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Skeleton } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/useToast';

import { agruparPermissoes } from './gruposDePermissao';

/**
 * Matriz de permissoes de um perfil.
 *
 * A tela mostrava a matriz e nao deixava mexer nela, o que transformava a
 * autorizacao em algo que so quem tem acesso ao banco consegue ajustar. Quem
 * administra a empresa precisa conseguir decidir o que cada perfil faz.
 *
 * Os grupos seguem a ordem do quadro de perfis do OnPoint (Kanban, Parametrizacao,
 * Propostas...). O perfil da corretora mostra so as permissoes que valem para ela:
 * conceder as outras nao mudaria nada para quem o recebe.
 *
 * O perfil que concede acesso total e o imutavel aparecem, e nao aceitam alteracao:
 * sem essa recusa existiria a empresa em que ninguem mais consegue administrar nada.
 */
export function PermissionMatrix({ perfil }: { perfil: Role }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const podeEditar = can('usuarios.papel.update');
  const publico = perfil.broker_scoped ? 'corretora' : undefined;

  const catalogo = useQuery({
    queryKey: ['permissions', publico ?? 'todas'],
    queryFn: ({ signal }) => permissions.list(signal, publico),
  });

  // O rascunho nasce do perfil escolhido, sem efeito: um efeito faria a matriz
  // renderizar uma vez vazia antes de marcar, o que aparece como piscada.
  const [rascunhoDe, setRascunhoDe] = useState<string | null>(null);
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set(perfil.permissions));
  if (rascunhoDe !== perfil.id) {
    setRascunhoDe(perfil.id);
    setMarcadas(new Set(perfil.permissions));
  }

  const salvar = useMutation({
    mutationFn: (chaves: string[]) => rolePermissions.set(perfil.id, chaves),
    onSuccess: (atualizado) => {
      toast.success(`Permissoes de ${atualizado.name} atualizadas.`);
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      // A sessao carrega as permissoes a cada requisicao, entao a mudanca vale
      // imediatamente para quem tem o perfil. A sessao propria tambem e relida.
      void queryClient.invalidateQueries({ queryKey: ['session'] });
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  if (catalogo.isPending) {
    return <Skeleton className="h-48 w-full" />;
  }

  const editavel = podeEditar && !perfil.grants_all && !perfil.is_immutable;
  const visiveis = new Set((catalogo.data ?? []).map((item) => item.key));
  const grupos = agruparPermissoes(catalogo.data ?? []);

  // Salvar envia o que esta marcado e e visivel na matriz. A permissao que o perfil
  // da corretora tinha e o escopo cortava sai: ela nao valia para quem o recebe.
  const conjunto = [...marcadas].filter((chave) => visiveis.has(chave));
  const mudou =
    conjunto.length !== perfil.permissions.length ||
    perfil.permissions.some((chave) => !conjunto.includes(chave));

  function alternar(chave: string, marcado: boolean) {
    setMarcadas((atual) => {
      const proximo = new Set(atual);
      if (marcado) proximo.add(chave);
      else proximo.delete(chave);
      return proximo;
    });
  }

  return (
    <div className="flex flex-col gap-4">
      {perfil.grants_all ? (
        <Alert tone="info" title="Este perfil concede acesso total">
          Ele existe para que a plataforma sempre tenha um caminho de administracao.
          Permitir que alguem lhe retire permissoes criaria a empresa em que ninguem
          mais consegue administrar nada.
        </Alert>
      ) : null}
      {perfil.is_immutable && !perfil.grants_all ? (
        <Alert tone="info" title="Este perfil e imutavel">
          As permissoes dele acompanham a plataforma e nao mudam por empresa.
        </Alert>
      ) : null}
      {!podeEditar && !perfil.grants_all ? (
        <Alert tone="info" title="Somente leitura">
          O seu acesso nao inclui a edicao de perfis.
        </Alert>
      ) : null}
      {perfil.broker_scoped ? (
        <p className="text-2xs text-ink-500">
          Perfil da corretora: a matriz mostra so as permissoes que valem para quem tem
          escopo de corretora.
        </p>
      ) : null}

      {grupos.map((grupo) => (
        <fieldset key={grupo.titulo} className="flex flex-col gap-2">
          <legend className="flex items-center gap-2 text-xs font-semibold text-ink-800">
            {grupo.titulo}
            <Badge tone="neutral">
              {grupo.itens.filter((item) => marcadas.has(item.key)).length} de{' '}
              {grupo.itens.length}
            </Badge>
          </legend>

          <div className="grid gap-1.5 sm:grid-cols-2">
            {grupo.itens.map((item) => (
              <label
                key={item.key}
                className={
                  editavel
                    ? 'flex cursor-pointer items-start gap-2 text-xs text-ink-700'
                    : 'flex items-start gap-2 text-xs text-ink-500'
                }
              >
                <input
                  type="checkbox"
                  disabled={!editavel}
                  checked={perfil.grants_all || marcadas.has(item.key)}
                  onChange={(evento) => alternar(item.key, evento.target.checked)}
                  className="mt-0.5 size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
                />
                <span className="min-w-0">
                  <span className="block truncate">{item.description || item.key}</span>
                  <span className="block truncate text-2xs text-ink-400">
                    {item.key}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      ))}

      {editavel ? (
        <div className="flex justify-end">
          <Button
            size="sm"
            disabled={!mudou || salvar.isPending}
            loading={salvar.isPending}
            onClick={() => salvar.mutate(conjunto)}
          >
            Salvar permissoes
          </Button>
        </div>
      ) : null}
    </div>
  );
}
